import React, { FC, useState, useEffect } from 'react';
import Modal from '~ComponentsImproved/Modal';
import { Box, Input, BlueButton, TransparentButton, SearchInputWrapper } from '~Styles';
import { useI18n, useLogger, useButton, useSelector, useDebounce } from '~Hooks';
import { AllUsers, AllUsersVariables, AllUsers_allUsers } from './__queries__';
import CreateNewUser from './CreateNewUser';
import styled from '@emotion/styled';
import { useLazyQuery } from '@apollo/client';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import { useCart } from '~Components/CartProvider';
import SelectedUser from './SelectedUser';
import { MEMBERS_QUERY } from './api';
import { useTableApi, useTableUniverse } from '~Components/TableUniverse';
import MembershipLevelBadge from './SelectedUser/MembershipLevelBadge';
import { DISCOUNT_TYPE, ROLE } from '../../../__queries__';
import { MEMBERSHIP_DISCOUNT_ID } from './consts';

const ModalContent = styled.div<{
  width?: string;
  showCreateUser: boolean;
  selectedUser: boolean;
}>`
  padding: 24px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: ${({ width }) => (width ? width : '600px')};
  max-width: calc(100% - 64px);
  margin: 0;
  height: ${({ showCreateUser, selectedUser }) =>
    selectedUser ? 'auto' : showCreateUser ? 'fit-content' : 'calc(100vh - 32px)'};
  max-height: calc(100vh - 16px);
  overflow-y: auto;

  /** Push modal up so iPad keyboard doesn't cover the input field */
  @media (min-height: 496px) {
    max-height: none;
    overflow-y: visible;
    margin: ${({ showCreateUser, selectedUser }) =>
      selectedUser ? '0 0 25% 0' : showCreateUser ? '16px 0 0 0' : '0 0 10% 0'};
  }

  @media (min-height: 680px) {
    margin: ${({ showCreateUser, selectedUser }) =>
      selectedUser ? '0 0 25% 0' : showCreateUser ? '16px 0 10% 0' : '40px 0 25% 0'};
  }
`;

const NotFoundBox = styled(Box)`
  text-align: center;
`;

const UserList = styled(Box)`
  height: 40vh;
  overflow-y: auto;
`;

const CreateNewUserButton = styled(BlueButton)`
  white-space: nowrap;
  width: 140px;
`;

const UserListItem = styled(TransparentButton)`
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 16px;
  text-transform: none;
  min-height: 64px;
`;

interface Props {
  isShowing: boolean;
  onClose: () => void;

  /** Only populated if we're in TSR */
  selectedCheque?: string | null;
  selectedChequeUserId?: string | null;

  /**
   * Set when relinking the user on an already placed order (e.g. from order
   * history) instead of setting the user on the active cart/cheque. Always
   * opens on the search view, and once a user is picked, offers "go back to
   * search" / "move order to this user" instead of the usual remove/connect
   * actions.
   */
  isEditingOrder?: boolean;
  onUserSelected?: (userId: string) => void;
}

/**
 * Selects a user from the user database & set it's userId for the order or cheque
 */
const LinkUser: FC<Props> = ({
  isShowing,
  selectedCheque,
  selectedChequeUserId,
  onClose,
  isEditingOrder,
  onUserSelected,
}) => {
  const { i18n } = useI18n();
  const logger = useLogger('link-user');
  const country = useSelector((state) => state.country);
  const cart = useCart();
  const tableApi = useTableApi();
  const tableUniverse = useTableUniverse();
  const isHandy = window.innerWidth <= 600;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    isEditingOrder ? null : selectedChequeUserId || null,
  );
  const [showCreateUser, setShowCreateUser] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [fetchMembers, membersQuery] = useLazyQuery<AllUsers, AllUsersVariables>(MEMBERS_QUERY, {
    fetchPolicy: 'no-cache',
  });

  // Debounced search term (300ms delay)
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Effect to trigger search when debounced term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim().length >= 2) {
      fetchMembers({
        variables: {
          filter: {
            OR: [
              { name_contains: debouncedSearchTerm.trim() },
              { email_contains: debouncedSearchTerm.trim() },
            ],
          },
          first: 10,
        },
      });
    }
  }, [debouncedSearchTerm, fetchMembers, logger]);

  const createUserButton = useButton({
    onClick: () => setShowCreateUser(true),
  });

  const handleUserSelect = (user: AllUsers_allUsers) => {
    setSelectedUserId(user.id);
    setSearchTerm('');
    logger.info('User selected', { userId: user.id, userName: user.name });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Get found users from query results
  const nonFilteredFoundUsers = membersQuery.data?.allUsers || [];
  const foundUsers = nonFilteredFoundUsers.filter(
    (user) => !(user.role === ROLE.CONTENT_ADMIN || user.venues.length > 0), // Filter out backoffice admins and users with venue assignments
  );
  const showResults = searchTerm.trim().length >= 2;

  const isInitialView = !showCreateUser && !selectedUserId;

  return (
    <Modal
      isShowing={isShowing}
      backdropClose={isInitialView || Boolean(selectedUserId)}
      hide={onClose}>
      <ModalContent
        showCreateUser={showCreateUser}
        selectedUser={Boolean(selectedUserId)}
        width={
          country === 'ES'
            ? isInitialView
              ? '480px'
              : '600px'
            : isInitialView
            ? '400px'
            : '480px'
        }>
        {showCreateUser ? (
          <CreateNewUser
            onUserCreated={(id) => {
              logger.info('New user created', { userId: id });
              setSelectedUserId(id);
              setShowCreateUser(false);
            }}
            onCancel={() => {
              setShowCreateUser(false);
            }}
          />
        ) : selectedUserId ? (
          <SelectedUser
            userId={selectedUserId}
            cancelLabel={isEditingOrder ? i18n('General.GoBack') : undefined}
            confirmLabel={isEditingOrder ? i18n('History.MoveOrderToUser') : undefined}
            onCancel={() => {
              setSelectedUserId(null);

              if (isEditingOrder) {
                return;
              }

              if (selectedCheque) {
                tableApi.applyDiscount(selectedCheque, {
                  rate: 0,
                  type: DISCOUNT_TYPE.PRICE,
                  selectedItemIds: [],
                  applyPersistentDiscount: true,
                });
                tableApi.setChequeUser(selectedCheque, null);
              } else {
                cart.clearUserData();
              }
              onClose();
            }}
            onConfirm={(memberDiscountRate?: number) => {
              if (isEditingOrder) {
                onUserSelected?.(selectedUserId);
                onClose();
                return;
              }

              selectedCheque
                ? tableApi.setChequeUser(selectedCheque, selectedUserId)
                : cart.setRegularUserId(selectedUserId);

              if (!memberDiscountRate) {
                onClose();
                return;
              }

              if (selectedCheque) {
                const chequeItems = tableUniverse.items.filter(
                  (item) => item.chequeId === selectedCheque,
                );
                tableApi.applyDiscount(selectedCheque, {
                  offerId: MEMBERSHIP_DISCOUNT_ID,
                  type: DISCOUNT_TYPE.PERCENTAGE,
                  rate: memberDiscountRate,
                  applyPersistentDiscount: true,
                  selectedItemIds: chequeItems
                    .filter((item) => !item.disableMembershipDiscount)
                    .map((item) => item.id),
                });
              } else {
                cart.setAppliedDiscount({
                  id: MEMBERSHIP_DISCOUNT_ID,
                  type: DISCOUNT_TYPE.PERCENTAGE,
                  value: memberDiscountRate,
                });
              }

              onClose();
            }}
          />
        ) : (
          <Box display="flex" flexDirection="column" gap="large" fluid>
            <Box
              display="flex"
              flexDirection="row"
              gap={isHandy ? 'xs' : 'small'}
              fluid
              alignItems="center">
              <SearchInputWrapper fluid value={searchTerm} onClear={() => setSearchTerm('')}>
                <Input
                  id="search-user"
                  name="search-user"
                  placeholder={i18n('Invoice.SearchUser') + '...'}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  dark
                  padding="small"
                  isSearch
                  autoFocus
                  fluid
                />
              </SearchInputWrapper>

              {country === 'ES' && (
                <>
                  <CreateNewUserButton size="small" {...createUserButton}>
                    {i18n('CustomerForCheckout.CreateNewCustomer')}
                  </CreateNewUserButton>
                </>
              )}
            </Box>

            <UserList display="flex" flexDirection="column" gap="xs" fluid>
              {membersQuery.loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" padding="large">
                  <ImpulseSpinner size={32} />
                </Box>
              ) : membersQuery.data && foundUsers.length === 0 ? (
                <NotFoundBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  padding="large">
                  <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)">
                    {i18n('CustomerForCheckout.NotFound')}
                  </Paragraph>
                </NotFoundBox>
              ) : !membersQuery.data && foundUsers.length === 0 ? (
                <NotFoundBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  padding="large">
                  <Paragraph level={isHandy ? 4 : 3} color="var(--colorBlueGray200)">
                    {i18n('CustomerForCheckout.StartSearching')}
                  </Paragraph>
                </NotFoundBox>
              ) : (
                foundUsers.map((user: AllUsers_allUsers) => (
                  <UserListItem
                    key={user.id}
                    display="flex"
                    justifyContent="space-between"
                    fluid
                    onClick={() => handleUserSelect(user)}>
                    <Box display="flex" flexDirection="column" gap="xs" alignItems="flex-start">
                      <Heading level={4} bottom={0} color="var(--colorWhite)">
                        {user.name}
                      </Heading>
                      {user.email && !isHandy && (
                        <Paragraph level={5} bottom={0} color="var(--colorBlueGray300)">
                          {user.email}
                        </Paragraph>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center">
                      {user.membershipLevel?.title?.en ? (
                        <MembershipLevelBadge
                          title={user.membershipLevel?.title?.en}
                          primaryColor={user.membershipLevel?.primaryColor}
                        />
                      ) : (
                        <Paragraph
                          level={isHandy ? 5 : 4}
                          bottom={0}
                          color="var(--colorBlueGray200)">
                          {user.membershipLevel?.title?.en ||
                            i18n('CustomerForCheckout.CustomerType.Private')}
                        </Paragraph>
                      )}
                    </Box>
                  </UserListItem>
                ))
              )}
            </UserList>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
};

export default LinkUser;
